import { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, Plus, Trash2 } from 'lucide-react';
import { notification, Modal } from 'antd';
import { useTranslation } from 'react-i18next';

const AvailabilitiesTab = () => {
  const { t } = useTranslation();
  const [availabilities, setAvailabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const VITE_SERVER_URL = import.meta.env.VITE_SERVER_URL || 'https://easyrdv.onrender.com';

  // État pour le formulaire de création de créneau
  const [showAvailabilityForm, setShowAvailabilityForm] = useState(false);
  const [availabilityForm, setAvailabilityForm] = useState({
    dayOfWeek: 1,
    startTime: '09:00',
    endTime: '12:00'
  });

  useEffect(() => {
    fetchAvailabilities();
  }, []);

  const fetchAvailabilities = async () => {
    try {
      const availabilitiesRes = await axios.get(`${VITE_SERVER_URL}/api/availability`);
      const availabilities = availabilitiesRes.data?.data?.availabilities || availabilitiesRes.data?.availabilities || [];
      setAvailabilities(availabilities);
    } catch (error) {
      console.error('Erreur lors du chargement des créneaux:', error);
      setAvailabilities([]);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour ajouter un nouveau créneau
  const addAvailability = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${VITE_SERVER_URL}/api/availability/add`, availabilityForm);
      setShowAvailabilityForm(false);
      setAvailabilityForm({ dayOfWeek: 1, startTime: '09:00', endTime: '12:00' });
      fetchAvailabilities();
      notification.success({
        message: 'Succès',
        description: 'Créneau ajouté avec succès',
        placement: 'topRight',
        style: {
          borderRadius: 12,
          boxShadow: '0 4px 24px rgba(80, 36, 255, 0.12)',
          background: 'linear-gradient(90deg, #6366f1 0%, #a21caf 100%)',
          color: '#fff',
          fontWeight: 500,
        },
        duration: 2.5,
      });
    } catch (error) {
      console.error('Erreur lors de l\'ajout du créneau:', error);
      notification.error({
        message: 'Erreur',
        description: error.response?.data?.message || 'Erreur lors de l\'ajout du créneau',
        placement: 'topRight',
        style: {
          borderRadius: 12,
          boxShadow: '0 4px 24px rgba(255, 36, 36, 0.12)',
          background: 'linear-gradient(90deg, #ef4444 0%, #be185d 100%)',
          color: '#fff',
          fontWeight: 500,
        },
        duration: 2.5,
      });
    }
  };

  // Fonction pour supprimer un créneau
  const deleteAvailability = async (availability) => {
    Modal.confirm({
      title: 'Supprimer le créneau',
      content: 'Êtes-vous sûr de vouloir supprimer ce créneau ?',
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      centered: true,
      onOk: async () => {
        try {
          await axios.delete(`${VITE_SERVER_URL}/api/availability/remove`, {
            data: {
              dayOfWeek: availability.dayOfWeek,
              startTime: availability.startTime,
              endTime: availability.endTime
            }
          });
          fetchAvailabilities();
          notification.success({
            message: 'Succès',
            description: 'Créneau supprimé avec succès',
            placement: 'topRight',
            style: {
              borderRadius: 12,
              boxShadow: '0 4px 24px rgba(80, 36, 255, 0.12)',
              background: 'linear-gradient(90deg, #6366f1 0%, #a21caf 100%)',
              color: '#fff',
              fontWeight: 500,
            },
            duration: 2.5,
          });
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          notification.error({
            message: 'Erreur',
            description: 'Erreur lors de la suppression du créneau',
            placement: 'topRight',
            style: {
              borderRadius: 12,
              boxShadow: '0 4px 24px rgba(255, 36, 36, 0.12)',
              background: 'linear-gradient(90deg, #ef4444 0%, #be185d 100%)',
              color: '#fff',
              fontWeight: 500,
            },
            duration: 2.5,
          });
        }
      },
    });
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[dayOfWeek];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-2 sm:px-4 py-6">
      <div className="bg-white shadow-lg rounded-2xl p-4 sm:p-8 border border-gray-100">
        {/* En-tête avec boutons */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h3 className="text-xl font-bold text-gray-900">{t('dashboard.availabilities.title')}</h3>
          <button
            onClick={() => setShowAvailabilityForm(true)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg shadow hover:from-blue-700 hover:to-blue-600 transition-colors text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <Plus className="w-5 h-5" />
            <span>{t('dashboard.availabilities.add')}</span>
          </button>
        </div>

        {/* Formulaire d'ajout de créneau */}
        {showAvailabilityForm && (
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl mb-6 shadow-sm">
            <h4 className="text-md font-semibold text-gray-800 mb-4">{t('dashboard.availabilities.new')}</h4>
            <form onSubmit={addAvailability} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('dashboard.availabilities.dayOfWeek')}</label>
                  <select
                    value={availabilityForm.dayOfWeek}
                    onChange={(e) => setAvailabilityForm({...availabilityForm, dayOfWeek: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm bg-white"
                  >
                    <option value={1}>{t('dashboard.availabilities.days.monday')}</option>
                    <option value={2}>{t('dashboard.availabilities.days.tuesday')}</option>
                    <option value={3}>{t('dashboard.availabilities.days.wednesday')}</option>
                    <option value={4}>{t('dashboard.availabilities.days.thursday')}</option>
                    <option value={5}>{t('dashboard.availabilities.days.friday')}</option>
                    <option value={6}>{t('dashboard.availabilities.days.saturday')}</option>
                    <option value={0}>{t('dashboard.availabilities.days.sunday')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('dashboard.availabilities.startTime')}</label>
                  <input
                    type="time"
                    value={availabilityForm.startTime}
                    onChange={(e) => setAvailabilityForm({...availabilityForm, startTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('dashboard.availabilities.endTime')}</label>
                  <input
                    type="time"
                    value={availabilityForm.endTime}
                    onChange={(e) => setAvailabilityForm({...availabilityForm, endTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm bg-white"
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-colors text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  {t('dashboard.availabilities.addBtn')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAvailabilityForm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg shadow hover:bg-gray-300 transition-colors text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  {t('dashboard.availabilities.cancel')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Liste des créneaux existants */}
        {availabilities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Clock className="h-12 w-12 text-gray-300 mb-2" />
            <h3 className="text-lg font-semibold text-gray-800">{t('dashboard.availabilities.noAvail')}</h3>
            <p className="mt-1 text-sm text-gray-500 text-center max-w-xs">
              {t('dashboard.availabilities.noAvailDesc')}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {Array.from({ length: 7 }, (_, dayOfWeek) => {
              const dayAvailabilities = availabilities.filter(a => a.dayOfWeek === dayOfWeek);
              if (dayAvailabilities.length === 0) return null;

              return (
                <div key={dayOfWeek} className="border border-gray-100 rounded-xl p-4 bg-gray-50 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-2 text-base flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-blue-400"></span>
                    {getDayName(dayOfWeek)}
                  </h3>
                  <div className="space-y-2">
                    {dayAvailabilities.map((availability, index) => (
                      <div key={`${dayOfWeek}-${index}`} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-2 bg-white rounded-lg border border-gray-200 shadow-sm gap-2">
                        <span className="text-sm text-gray-700 font-medium">
                          {availability.startTime} - {availability.endTime}
                        </span>
                        <button
                          onClick={() => deleteAvailability(availability)}
                          className="text-red-600 hover:text-white hover:bg-red-600 border border-red-100 px-3 py-1 rounded-lg text-xs sm:text-sm flex items-center gap-1 transition-colors font-semibold focus:outline-none focus:ring-2 focus:ring-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>{t('dashboard.availabilities.delete')}</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailabilitiesTab; 